import React, { useEffect, useRef, useState } from "react";
import EditorJS from "@editorjs/editorjs";
import Header from "@editorjs/header";
import Paragraph from "@editorjs/paragraph";
import List from "@editorjs/list";
import Quote from "@editorjs/quote";
import CallToAction from "../editorjs/CallToAction";
import ImageBlock from "../editorjs/ImageBlock";
import YoutubeEmbed from "../editorjs/YoutubeEmbed";
import CodeBlock from "../editorjs/CodeBlock";
import ModelBlock from "../editorjs/ModelBlock";
import ClearFormatting from "../editorjs/ClearFormatting";
import GithubGist from "editorjs-github-gist-plugin";
import Delimiter from "@editorjs/delimiter";
import { loadModelViewer } from "../utils/loadModelViewer";
import MonoButton from "./MonoButton";
import ReactMarkdown from "react-markdown";
import getPostBlockingSchema from "../utils/getPostBlockingSchema";
import useDynamicMeta from "../hooks/useDynamicMeta";

// MDX Schema
import { blocksToMDX } from "../utils/blocksToMDX";

// Slug-Generator
const generateSlug = (input) =>
  input.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// EditorJS-Blocks bereinigen
function cleanEditorJsBlocks(data) {
  const cleanText = (text) =>
    (typeof text === "string" ? text : "")
      .replace(/<[^>]+>/g, "")
      .replace(/[*#]/g, "")
      .replace(/&gt;/g, ">")
      .replace(/&lt;/g, "<")
      .replace(/&amp;/g, "&")
      .replace(/&nbsp;/g, " ")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&mdash;/g, "—")
      .replace(/&ndash;/g, "–");

  return data.map((block) => {
    const cleaned = { ...block };
    if (cleaned.data?.text) cleaned.data.text = cleanText(cleaned.data.text);
    if (Array.isArray(cleaned.data?.items)) {
      cleaned.data.items = cleaned.data.items.map((item) =>
        typeof item === "string"
          ? cleanText(item)
          : item?.content
          ? { ...item, content: cleanText(item.content) }
          : item
      );
    }
    return cleaned;
  });
}

// Klartext + Markdown
function getPlainTextAndMarkdown(data) {
  const blocks = data?.blocks || [];
  const plainArr = [];
  const mdArr = [];

  blocks.forEach(({ type, data }) => {
    let txt = "";
    let md = "";

    switch (type) {
      case "header":
        txt = data.text || "";
        md = `## ${data.text || ""}`;
        break;
      case "paragraph":
        txt = data.text || "";
        md = data.text || "";
        break;
      case "quote":
        txt = data.text || "";
        md = `> ${data.text || ""}`;
        break;
      case "list":
        const items = data.items || [];
        const flat = items.map((i) =>
          typeof i === "string" ? i : i.content || ""
        );
        txt = flat.join("\n");
        md = flat.map((i) => `- ${i}`).join("\n");
        break;
      case "code":
        txt = data.content || "";
        md = `\`\`\`${data.language || ""}\n${data.content || ""}\n\`\`\``;
        break;
      case "delimiter":
        txt = "---";
        md = "---";
        break;
      case "image":
        const alt = data.alt?.trim() || data.caption?.trim() || "Bild";
        const url = data.file?.url || data.url || data.src || "";
        if (url) {
          txt = `[Bild: ${alt}] ${url}`;
          md = `![${alt}](${url})`;
        }
        break;
      case "youtube":
        txt = data.url ? `[YouTube: ${data.url}]` : "";
        md = data.url ? `🔗 ${data.url}` : "";
        break;
      case "gist":
        txt = data.url ? `[GitHub Gist: ${data.url}]` : "";
        md = data.url ? `🔗 ${data.url}` : "";
        break;
      case "cta":
        txt = data.content || "";
        md = `**${data.content || ""}**`;
        break;
      case "model3d":
        txt = data.title || "3D‑Modell";
        md = `📦 ${txt}`;
        break;
      default:
        break;
    }

    if (txt) plainArr.push(txt);
    if (md) mdArr.push(md);
  });

  return {
    plain: plainArr.join("\n\n"),
    markdown: mdArr.join("\n\n")
  };
}

// Datei herunterladen
function downloadFile(data, type, filename) {
  const blob = new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Hauptkomponente
export default function SmartCaseEditor() {
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [image, setImage] = useState("");
  const [editorData, setEditorData] = useState(null);
  const [availableArticles, setAvailableArticles] = useState([]);
  const [markdown, setMarkdown] = useState("");
  const ejInstance = useRef(null);
  const slug = generateSlug(title);

  useDynamicMeta({
    title,
    description: subtitle,
    image,
    schemaJson: getPostBlockingSchema({
      title,
      subtitle,
      date,
      image,
      markdown,
      slug,
    }),
  });

  // Liste laden
  useEffect(() => {
    fetch("https://www.denisbitter.de/stage01/api/case.php")
      .then((res) => res.json())
      .then(setAvailableArticles)
      .catch(console.error);
  }, []);

  // EditorJS initialisieren
  useEffect(() => {
    loadModelViewer();

    const timeout = setTimeout(() => {
      ejInstance.current = new EditorJS({
        holder: "editorjs",
        autofocus: true,
        tools: {
          delimiter: Delimiter,
          header: {
            class: Header,
            inlineToolbar: ["link", "bold", "italic", "clearFormatting"],
            config: { levels: [2, 3, 4], defaultLevel: 2 },
          },
          paragraph: {
            class: Paragraph,
            inlineToolbar: ["link", "bold", "italic", "clearFormatting"],
          },
          list: List,
          quote: Quote,
          cta: CallToAction,
          image: ImageBlock,
          youtube: YoutubeEmbed,
          code: CodeBlock,
          model3d: { class: ModelBlock, toolbox: ModelBlock.toolbox },
          clearFormatting: ClearFormatting,
          gist: {
            class: GithubGist,
            config: {
              endpoint: "https://api.github.com/gists",
            },
          },
        },
        onReady: () => console.log("✅ Editor.js ready"),
      });
    }, 0);

    return () => {
      clearTimeout(timeout);
      if (ejInstance.current && typeof ejInstance.current.destroy === "function") {
        ejInstance.current.destroy();
        ejInstance.current = null;
      }
    };
  }, []);

  // Speichern
const handleSave = async () => {
  if (!ejInstance.current) return;

  // 1. EditorJS-Daten holen und bereinigen
  const output = await ejInstance.current.save();
  const cleaned = cleanEditorJsBlocks(output.blocks);
  setEditorData({ blocks: cleaned });

  // 2. Markdown + MDX erzeugen
  const { markdown: plainMarkdown } = getPlainTextAndMarkdown({ blocks: cleaned });
  const mdx = blocksToMDX(cleaned);

  setMarkdown(mdx); // Vorschau basiert ab jetzt auf echtem MDX

  // 3. Endpunkt wählen
  const endpoint = availableArticles.includes(slug)
    ? "https://www.denisbitter.de/stage01/api/case_update.php"
    : "https://www.denisbitter.de/stage01/api/case_add.php";

  // 4. Daten senden
  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        slug,
        title,
        subtitle,
        date,
        image: image || `https://www.denisbitter.de/images/${slug}.jpg`,
        blocks: cleaned,
        mdx, // 🟢 MDX wird nun mitgesendet
      }),
    });

    if (!response.ok) throw new Error("Fehler beim Speichern");

    alert("✅ Case gespeichert!");
  } catch (err) {
    console.error("Fehler beim Speichern:", err);
    alert("❌ Fehler beim Speichern.");
  }
};


  const downloadPlain = () => {
    const plain = getPlainTextAndMarkdown(editorData).plain;
    downloadFile(plain, "text/plain", `${slug}-klartext.txt`);
  };

  const downloadMd = () => {
    downloadFile(markdown, "text/markdown", `${slug}-post.md`);
  };

  // Einzelnen Case laden
  const loadJson = async (slugValue) => {
    const res = await fetch(
      `https://www.denisbitter.de/stage01/api/case.php?slug=${slugValue}`
    );
    const json = await res.json();
    const raw = json.blocks || json.content?.blocks || [];
    const blocks = raw.map((b) =>
      b.type === "model" ? { ...b, type: "model3d" } : b
    );
    ejInstance.current.render({ blocks });
    setEditorData({ blocks });

    const { markdown: md } = getPlainTextAndMarkdown({ blocks });
    setMarkdown(md);

    setTitle(json.title);
    setSubtitle(json.subtitle);
    setDate(json.date);
    setImage(json.image);
  };

  return (
    <div className="grid md:grid-cols-2 gap-6 p-6 bg-[#fff9f1] mt-9">
      <div className="space-y-4">
        <h2 className="text-xs">CaseEditor</h2>
        <select
          onChange={(e) => e.target.value && loadJson(e.target.value)}
          className="border p-1 w-full mb-2 text-xs"
        >
          <option value="">Case wählen…</option>
          {availableArticles.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>

        <input
          placeholder="Titel"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="border p-1 w-full mb-1 text-xs"
        />
        <input
          placeholder="Untertitel"
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          className="border p-1 w-full mb-1 text-xs"
        />
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="border p-1 w-full mb-1 text-xs"
        />
        <input
          placeholder="Bild-URL"
          value={image}
          onChange={(e) => setImage(e.target.value)}
          className="border p-1 w-full mb-2 text-xs"
        />

        <div id="editorjs" className="min-h-[300px] border mb-2 text-xs"></div>
        <MonoButton className="text-xs" onClick={handleSave}>
          💾 Speichern & Markdown erzeugen
        </MonoButton>
      </div>

      <div>
        <h2 className="text-xs">Markdown-Vorschau</h2>
        <div className="border p-4 mb-2 max-h-[300px] overflow-auto text-xs">
          <ReactMarkdown>
            {markdown || "_Hier erscheint dein Markdown…_"}
          </ReactMarkdown>
        </div>

        <MonoButton onClick={downloadPlain} className="mr-2 text-xs">
          📥 Klartext herunterladen
        </MonoButton>
        <MonoButton onClick={downloadMd} className="text-xs">
          📥 Markdown herunterladen
        </MonoButton>

        <h4 className="mt-4 text-xs">🔍 JSON Debug Output</h4>
        <pre className="border p-2 max-h-[200px] overflow-auto text-xs">
          {editorData
            ? JSON.stringify(cleanEditorJsBlocks(editorData.blocks), null, 2)
            : "// Noch nichts geladen"}
        </pre>
      </div>
    </div>
  );
}
